/**
 * Skill tree — three branches of ten, per Calling.
 *
 * The data is three strictly linear chains (`canUnlock` requires tier n-1 of the same
 * branch), so three columns map onto it exactly; a radial would spend a lot of geometry
 * hiding a straight line. Tier 4 and 7 are ability modifiers, tier 9 is the branch
 * keystone, everything else is a stat node.
 */

import type { VNode } from 'preact';
import { useState } from 'preact/hooks';
import {
  CALLINGS,
  SKILL_TREES,
  canUnlock,
  resolveModifiers,
  type SkillNode,
} from '@stillsun/shared';
import { respecSkills, unlockNode } from '../../net/progress-api.js';
import { progressStore } from '../../net/progress-store.js';
import {
  type HubController,
  LEDGER_SILENT,
  Loading,
  Notice,
  Section,
  errText,
  useProgress,
} from './common.js';

export interface SkillsProps {
  ctl: HubController;
  nonce: number;
  token: string;
}

/** Human labels for the handful of `PlayerModifiers` fields a node can move. */
const MOD_LABELS: Record<string, string> = {
  maxHp: 'max HP',
  maxStamina: 'max stamina',
  speedMult: 'movement speed',
  sprintDrainMult: 'sprint drain',
  recoilMult: 'recoil',
  spreadMult: 'spread',
  damageMult: 'damage',
  headshotBonus: 'headshot bonus',
  reloadMult: 'reload time',
  magBonus: 'magazine',
  backpackSlots: 'backpack slots',
  quickSlots: 'quick slots',
  healMult: 'healing',
  reviveSpeed: 'revive speed',
  noiseMult: 'noise',
  staggerResist: 'stagger resist',
  modEffectMult: 'mod effect',
  cooldownMult: 'ability cooldowns',
};

/** Fields expressed as fractions rather than flat additions. */
const PERCENT_FIELDS = new Set([
  'speedMult',
  'sprintDrainMult',
  'recoilMult',
  'spreadMult',
  'damageMult',
  'headshotBonus',
  'reloadMult',
  'healMult',
  'reviveSpeed',
  'noiseMult',
  'staggerResist',
  'modEffectMult',
  'cooldownMult',
]);

function summariseMods(mods: Record<string, number>): string[] {
  const out: string[] = [];
  for (const [key, value] of Object.entries(mods)) {
    const label = MOD_LABELS[key];
    if (label === undefined || value === 0) continue;
    const pct = PERCENT_FIELDS.has(key);
    const shown = pct
      ? `${value > 0 ? '+' : ''}${String(Math.round(value * 100))}%`
      : `${value > 0 ? '+' : ''}${String(value)}`;
    out.push(`${shown} ${label}`);
  }
  return out;
}

export function Skills(props: SkillsProps): VNode {
  const { ctl, token } = props;
  // Skills is the inside of the chosen Calling card, not a sibling tab any more, so every
  // exit from here goes back to step I rather than to a strip that no longer lists it.
  const back = (
    <button
      type="button"
      class="hub-btn hub-btn-quiet"
      onClick={() => {
        ctl.setTab('calling');
      }}
    >
      Back to Calling
    </button>
  );
  const progress = useProgress(token, props.nonce);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [hovered, setHovered] = useState<SkillNode | null>(null);

  if (progress.loading && !progress.loaded) {
    return (
      <div class="hub-pane">
        <Loading />
      </div>
    );
  }
  if (progress.data === null) {
    return (
      <div class="hub-pane">
        <Notice>{LEDGER_SILENT}</Notice>
        {back}
      </div>
    );
  }

  const data = progress.data;
  const calling = data.calling;
  const tree = SKILL_TREES[calling];
  const def = CALLINGS[calling];
  if (!tree || !def) {
    return (
      <div class="hub-pane">
        <Notice>Pick a Calling first — step I decides which tree you climb.</Notice>
        {back}
      </div>
    );
  }

  // Only this Calling's nodes count: progress is per Calling, and the server may be
  // holding unlocks for the other three at the same time.
  const mine = new Set(data.unlockedNodes.filter((id) => id.startsWith(`${String(calling)}-`)));
  const points = data.skillPoints;
  const resolved = resolveModifiers(def.passive, tree, mine);
  const totals = summariseMods(resolved as unknown as Record<string, number>);

  const unlock = (node: SkillNode): void => {
    if (busy || points <= 0 || !canUnlock(tree, mine, node.id)) return;
    setBusy(true);
    setNote('');
    const before = data.unlockedNodes;
    progressStore.patch({
      unlockedNodes: [...before, node.id],
      skillPoints: Math.max(0, points - 1),
    });
    void (async () => {
      try {
        await unlockNode(token, node.id);
        await progressStore.load(token, true);
      } catch (err) {
        progressStore.patch({ unlockedNodes: before, skillPoints: points });
        setNote(errText(err, 'That node would not take.'));
      } finally {
        setBusy(false);
      }
    })();
  };

  const respec = (): void => {
    if (busy) return;
    setBusy(true);
    setNote('');
    void (async () => {
      try {
        await respecSkills(token);
        await progressStore.load(token, true);
      } catch (err) {
        setNote(errText(err, 'The Compact would not undo that.'));
      } finally {
        setBusy(false);
      }
    })();
  };

  const branchNodes = (branch: number): SkillNode[] =>
    tree.nodes.filter((n) => n.branch === branch).sort((a, b) => a.tier - b.tier);

  return (
    <div class="hub-pane">
      {note.length > 0 ? <p class="hub-error">{note}</p> : null}

      <Section
        title={`${def.name} — ${String(mine.size)}/${String(tree.nodes.length)}`}
        hint={
          points > 0
            ? `${String(points)} point${points === 1 ? '' : 's'} to spend`
            : 'No points to spend'
        }
      >
        <div class="hub-skillhead">
          <p class="hub-notice">
            One point per level. A node needs the one below it in the same branch; the last node of
            each branch is its keystone.
          </p>
          {back}
          <button
            type="button"
            class="hub-btn hub-btn-quiet"
            disabled={busy || mine.size === 0}
            onClick={respec}
          >
            Respec (costs Wick)
          </button>
        </div>

        <div class="hub-tree">
          {[0, 1, 2].map((branch) => (
            <div key={branch} class="hub-branch">
              <h3 class="hub-branch-title">{tree.branches[branch] ?? ''}</h3>
              {/* Top of the column is the keystone, so the column reads as a climb. */}
              {[...branchNodes(branch)].reverse().map((node) => {
                const owned = mine.has(node.id);
                const open = !owned && canUnlock(tree, mine, node.id);
                const affordable = open && points > 0;
                return (
                  <button
                    key={node.id}
                    type="button"
                    class={`hub-node ${node.kind}${owned ? ' owned' : ''}${open ? ' open' : ''}`}
                    disabled={busy || !affordable}
                    aria-label={`${node.name} — ${node.desc}`}
                    onMouseEnter={() => {
                      setHovered(node);
                    }}
                    onFocus={() => {
                      setHovered(node);
                    }}
                    onMouseLeave={() => {
                      setHovered(null);
                    }}
                    onClick={() => {
                      unlock(node);
                    }}
                  >
                    <span class="hub-node-tier">{String(node.tier + 1)}</span>
                    <span class="hub-node-name">{node.name}</span>
                    {node.kind === 'keystone' ? <span class="hub-node-flag">keystone</span> : null}
                    {node.kind === 'ability' ? <span class="hub-node-flag">ability</span> : null}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        <div class="hub-skillfoot">
          <div class="hub-nodedesc">
            {hovered ? (
              <>
                <b>{hovered.name}</b>
                <span>{hovered.desc}</span>
              </>
            ) : (
              <span class="hub-hint">Hover a node to read what it does.</span>
            )}
          </div>
          <div class="hub-totals">
            <span class="hub-label">Your build</span>
            {totals.length === 0 ? (
              <span class="hub-hint">Nothing spent yet.</span>
            ) : (
              totals.map((t) => (
                <span key={t} class="hub-chip static">
                  {t}
                </span>
              ))
            )}
            {resolved.flags.map((f) => (
              <span key={f} class="hub-chip static flag">
                {f}
              </span>
            ))}
          </div>
        </div>
      </Section>
    </div>
  );
}
